import { Icon, IconColor } from '@/dc-shared-web/src/components/base/Icon'
import { DeleteFolderDialog } from '@/app/(dashboard)/folders/DeleteFolderDialog'
import { RenameFolderDialog } from '@/app/(dashboard)/folders/RenameFolderDialog'
import {
  DropdownMenuContent,
  Folder,
  useFSTrackEvent,
  DropdownMenuItem,
  DropdownMenu,
  DropdownMenuTrigger,
  Button,
} from '@/dc-shared-web/src'
import { faEllipsisVertical } from '@document-crunch/pro-regular-svg-icons'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { MoveDialog } from '@/app/(dashboard)/projects/MoveDialog'
import { FoldersListItem } from '@/dc-shared-web/src/hooks/api/folder/types'
import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'

interface FolderActionsPropsNew {
  folder: FoldersListItem
  shouldShowActions: boolean
  teamUuid: string
  initialLocation: LocationType
}

export const FolderActions = ({
  folder,
  shouldShowActions,
  teamUuid,
  initialLocation,
}: FolderActionsPropsNew) => {
  const queryClient = useQueryClient()
  const [selectedAction, setSelectedAction] = useState<
    'rename' | 'delete' | 'move' | null
  >(null)

  const { trackEvent } = useFSTrackEvent()

  const handleDialogClose = () => setSelectedAction(null)

  const handleDialogSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['FolderForMove'],
    })

    setSelectedAction(null)
  }

  const handleDeleteDialogSuccess = async () => {
    setSelectedAction(null)

    /* We invalidate the query here and not in the hook so that we can redirect back to the dashboard once the delete is successful without rendering the NotFound component which logs the user out of the app. */
    await queryClient.invalidateQueries({
      queryKey: ['folders', 'list'],
    })
    await queryClient.invalidateQueries({ queryKey: ['foldersList'] })
    await queryClient.invalidateQueries({ queryKey: ['FolderForMoveTree'] })
    await queryClient.invalidateQueries({
      queryKey: ['FolderForMove'],
    })
  }

  const handleMoveFolder = () => {
    trackEvent(FS.FOLDER_ACTIONS_MOVE_FOLDER, {
      folder_id: folder.uuid,
      folder_name: folder.name,
      team: teamUuid,
    })
    setSelectedAction('move')
  }

  if (!shouldShowActions) {
    return null
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='mini'
            aria-haspopup='menu'
            aria-expanded={Boolean(open)}
            data-testid='folder-actions-trigger'
          >
            <span className='tw-sr-only'>Folder actions</span>
            <Icon
              icon={faEllipsisVertical}
              color={IconColor.tertiary}
              size='xl'
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => setSelectedAction('rename')}
            data-testid='folder-action-rename'
          >
            Rename folder
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleMoveFolder()}
            data-testid='folder-action-move'
          >
            Move folder
          </DropdownMenuItem>
          {!folder.crunch_children_count &&
            !folder.folder_children_count &&
            !folder.project_children_count && (
              <DropdownMenuItem
                onClick={() => setSelectedAction('delete')}
                data-testid='folder-action-delete'
              >
                Delete folder
              </DropdownMenuItem>
            )}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedAction === 'rename' && (
        <RenameFolderDialog
          folder={folder}
          teamUuid={teamUuid}
          onDialogClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
        />
      )}
      {selectedAction === 'move' && (
        <MoveDialog
          folder={folder}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
          type='folder'
          teamUuid={teamUuid}
          open={selectedAction === 'move'}
          initialLocation={initialLocation}
        />
      )}
      {selectedAction === 'delete' && (
        <DeleteFolderDialog
          folder={folder}
          teamUuid={teamUuid}
          onDialogClose={handleDialogClose}
          onDeleteSuccess={handleDeleteDialogSuccess}
        />
      )}
    </>
  )
}
